/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import clsx from 'clsx';
import {translate} from '@ion/core/src/locale/locale';
import {Device, EntityStatus, GenericObject} from '@ion/core/src/types/entities';
import {isMDTDevice as isValidMDTDevice} from '@ion/core/src/utils/DeviceUtils';
import Divider from '@ion/dls/src/components/divider/Divider';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {NewIcon} from '@ion/dls/src/icons/NewIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {
  deviceDetailSelector,
  devicePermissionsSelector,
  devicesViewSelector
} from '../../store/selectors/devices';
import {AlertHistoryTab} from './AlertHistoryTab';
import CarbTab from './CarbTab';
import {DeviceDetailsTab} from './DeviceDetailsTab';
import {DeviceInfoPanel} from './DeviceInfoPanel';
import {EventsTab} from './EventsTab';
import {FilesTab} from './FilesTab';
import {HealthEventsTab} from './HealthEventsTab';
import {IdReportTab} from './IdReportTab';
import {ManufacturingTab} from './ManufacturingTab';
import {SCISettingsTab} from './SCISettingsTab';
import {hasPermission} from './SCISettingsUtils';
import SentinelAssociationTab, {hasSentinelAssociationTab} from './SentinelAssociationTab';
import {ServicesTab} from './ServicesTab';
import {ShippingWarrantyTab} from './ShippingWarrantyTab';
import {DeviceEditorTabs} from './types';
import {useSentinelAssociationNewBadge} from './useSentinelAssociationNewBadge';
import {
  ViewDeviceEditorProps,
  viewDeviceStyles
} from './ViewDeviceEditor';
import {ViewDeviceTopMenuProps} from './ViewDeviceTopMenu';

export interface BulkViewDeviceEditorProps extends ViewDeviceEditorProps,
  Pick<ViewDeviceTopMenuProps, 'onRefresh'> {
  populatedDevice?: Device | Device[];
  currentFileHistory?: GenericObject[];
  hasTeleCryptEnabled?: boolean;
  deviceIdentifier?: any;
};

const {
  DETAILS,
  FILES,
  SERVICES,
  ID_REPORT,
  HEALTH_EVENTS,
  TELEMETRY_EVENTS,
  SENTINEL_ASSOCIATION,
  CARB,
  ALERT_HISTORY,
  MANUFACTURING,
  SHIPPING,
  SCI_SETTINGS
} = DeviceEditorTabs;

/**
 * Display Bulk View Device Editor on click on view option from grid menu
 */
export const BulkViewDeviceEditor = ({
  onRefresh,
  populatedDevice,
  deviceIdentifier,
  selectedJobId,
  devicesPermissions,
  Features,
  explicitReportDate = false,
  isToggleSwitchGrid = false,
  isVbuFwEnabled = false,
  hasCARBEnabled = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  hasTeleCryptEnabled = false,
  isShowRefreshButton = true
}: BulkViewDeviceEditorProps) => {
  const editorStyles                = useExtendStyles(viewDeviceStyles);
  const classes                     = editorStyles();
  const deviceDetail                = useSelector(deviceDetailSelector);
  const [scriptDesc, setScriptDesc] = useState('');
  const [activeTab, setActiveTab]   = useState(0);
  const hasCarbServiceEnabled       = hasCARBEnabled && (deviceDetail?.features?.CARB === EntityStatus.Enabled);
  const hasSentinelAssociation      = hasSentinelAssociationTab(populatedDevice);
  const {
    showNewBadge: showSentinelNewBadge,
    onSentinelTabViewed
  } = useSentinelAssociationNewBadge(hasSentinelAssociation);

  const {
    result,
    latestIdReport,
    SCIDeviceData,
    isSCIDevice
  } = useSelector(devicesViewSelector);

  const {permissions} = useSelector(devicePermissionsSelector);
  const isLMU55Device = deviceDetail?.type?.title?.toLowerCase()?.includes('lmu55');
  const isMDTDevice   = isValidMDTDevice(deviceDetail);

  function handleChange(event, newValue) {
    setActiveTab(newValue);
  };

  const SCIRead = useMemo((): boolean => {
    return hasPermission(permissions, 'read', 'deviceCommand');
  }, [permissions]);

  const SCIUpdate = useMemo((): boolean => {
    return hasPermission(permissions, 'update', 'deviceCommand');
  }, [permissions]);

  const hasSCIPermission = useMemo(() => (
    SCIRead || SCIUpdate
  ), [permissions]);

  const tabs = useMemo(() => translate('admin-devices:view.tabs', {returnObjects: true}), []);
  useEffect(() => {
    if (activeTab === SENTINEL_ASSOCIATION && hasSentinelAssociation) {
      onSentinelTabViewed();
    }
  }, [activeTab, hasSentinelAssociation, onSentinelTabViewed]);
  return (
    <>
      <div>
        <DeviceInfoPanel device={populatedDevice}
          deviceDetail={result as Device}
          latestIdReport={latestIdReport}
          isCtcAdmin={isCtcAdmin}
          explicitReportDate={explicitReportDate} />
      </div>
      <div className={classes.contentContainer}>
        <div>
          <div className={classes.stickyTabs}>
            <Tabs className={classes.customTab}
              value={activeTab}
              onChange={handleChange}
              variant='scrollable'
              TabIndicatorProps={{
                style : {
                  transition : 'none'
                }
              }}>
              <Tab value={DETAILS} label={tabs.deviceDetails} disableRipple />
              <Tab value={FILES} label={tabs.files} disableRipple />
              {!isMDTDevice && (
                <Tab value={SERVICES} label={tabs.services} disableRipple />
              )}
              <Tab value={ID_REPORT} label={tabs.idReport} disableRipple />
              {isLMU55Device && (
                <Tab value={HEALTH_EVENTS} label={tabs.healthEvent} disableRipple />
              )}
              <Tab value={TELEMETRY_EVENTS} label={tabs.events} disableRipple />
              {hasSentinelAssociation && (
                <Tab value={SENTINEL_ASSOCIATION}
                  icon={
                    <Render.If condition={showSentinelNewBadge}>
                      <div className={classes.iconWrapper}>
                        <NewIcon size={IconSize.largeMedium} color={dlsColor('caGreen')} />
                      </div>
                    </Render.If>
                  }
                  className={clsx(classes.tabStyle, classes.tabRoot)}
                  label={tabs.sentinel}
                  disableRipple />
              )}
              {hasCarbServiceEnabled && (
                <Tab value={CARB}
                  icon={
                    <div className={classes.iconWrapper}>
                      <NewIcon size={IconSize.largeMedium} color={dlsColor('caGreen')} />
                    </div>
                  }
                  className={clsx(classes.tabStyle, classes.tabRoot)}
                  label={tabs.carb}
                  disableRipple />
              )}
              <Tab value={ALERT_HISTORY} label={tabs.alertHistory} disableRipple />
              <Tab value={MANUFACTURING} label={tabs.mfgData} disableRipple />
              <Tab value={SHIPPING} label={tabs.shippingWarranty} disableRipple />
              {hasSCIPermission && SCIDeviceData?.sciDeviceSettings && (
                <Tab value={SCI_SETTINGS} label={tabs.sciSettings} disableRipple />
              )}
            </Tabs>
            <Divider className={classes.customDivider} />
          </div>
          <>
            <Render.If condition={activeTab === DETAILS}>
              <DeviceDetailsTab deviceDetail={result as Device}
                Features={Features}
                isCurrentDeviceMDT={isMDTDevice}
                devicesPermissions={devicesPermissions}
                isVbuFwEnabled={isVbuFwEnabled}
                tabIndex={DETAILS}
                device={populatedDevice}
                onSuccess={onRefresh}
                scriptDesc={scriptDesc} />
            </Render.If>

            <Render.If condition={activeTab === FILES}>
              <FilesTab tabIndex={FILES}
                selectedJobId={selectedJobId}
                device={deviceDetail}
                deviceIdentifier={deviceIdentifier}
                isToggleSwitchGrid={isToggleSwitchGrid}
                isShowColumnChooserIcon={isShowColumnChooserIcon}
                isShowRefreshButton={isShowRefreshButton}
                setScriptDesc={setScriptDesc} />
            </Render.If>

            <Render.If condition={!isMDTDevice && activeTab === SERVICES}>
              <ServicesTab hasTeleCryptEnabled={hasTeleCryptEnabled}
                Features={Features}
                device={populatedDevice}
                isCtcAdmin={isCtcAdmin}
                tabIndex={SERVICES} />
            </Render.If>
            
            <Render.If condition={activeTab === ID_REPORT}>
              <IdReportTab isMDT={isMDTDevice}
                tabIndex={ID_REPORT} />
            </Render.If>

            <Render.If condition={isLMU55Device && activeTab === HEALTH_EVENTS}>
              <HealthEventsTab tabIndex={HEALTH_EVENTS}
                device={populatedDevice}/>
            </Render.If>

            <Render.If condition={activeTab === TELEMETRY_EVENTS}>
              <EventsTab tabIndex={TELEMETRY_EVENTS}
                device={deviceDetail} />
            </Render.If>
            
            <Render.If condition={hasSentinelAssociation && activeTab === SENTINEL_ASSOCIATION}>
              <SentinelAssociationTab tabIndex={SENTINEL_ASSOCIATION}
                tabName={tabs.sentinel}
                deviceIdentifier={deviceIdentifier || deviceDetail?.id}
                currentDevice={populatedDevice} />
            </Render.If>

            <Render.If condition={hasCarbServiceEnabled && activeTab === CARB}>
              <CarbTab tabIndex={CARB}
                tabName={tabs.carb}
                currentDevice={deviceDetail} />
            </Render.If>

            <Render.If condition={activeTab === ALERT_HISTORY}>
              <AlertHistoryTab tabIndex={ALERT_HISTORY}
                device={populatedDevice} />
            </Render.If>
            
            <Render.If condition={activeTab === MANUFACTURING}>
              <ManufacturingTab isCMF={isMDTDevice}
                deviceIdentifier={deviceIdentifier}
                isMDTDevice={isMDTDevice}
                tabIndex={MANUFACTURING} />
            </Render.If>

            <Render.If condition={activeTab === SHIPPING}>
              <ShippingWarrantyTab tabIndex={SHIPPING}
                deviceIdentifier={deviceIdentifier} />
            </Render.If>
            
            <Render.If condition={SCIDeviceData?.sciDeviceSettings && activeTab === SCI_SETTINGS}>
              <SCISettingsTab SCIDeviceData={SCIDeviceData}
                tabIndex={SCI_SETTINGS}
                noUpdate={!SCIUpdate}
                serialNumber={populatedDevice?.serialNumber}
                hasSciPermission={hasSCIPermission} />
            </Render.If>
          </>
        </div>
        <div className={classes.center}>
          <Render.If condition={isSCIDevice && !SCIDeviceData}>
            <ContentLoader />
          </Render.If>
        </div>
      </div>
    </>
  );
};

export default BulkViewDeviceEditor;
